import { ApiService } from "../../../config";

export type SeoUsage = "servico" | "produto" | "post" | "qa" | "review";

export type SeoResearchItem = {
  keyword: string;
  nome: string;
  descricao: string;
  uso: SeoUsage;
  intencao: "alta" | "media" | "baixa";
  prioridade: number;
  confirmar: boolean;
  motivoConfirmar: string;
};

export type SeoResearch = {
  diagnostico: string;
  grupos: Array<{ grupo: string; keywords: string[] }>;
  itens: SeoResearchItem[];
  produtos: Array<{ nome: string; cobre: string[]; descricao: string }>;
  georanking: { p1: string[]; p2: string[]; p3: string[] };
  usoEstrategico: {
    servicos: string[];
    produtos: string[];
    posts: string[];
    qa: string[];
    avaliacoes: string[];
  };
  confirmarAntes: string[];
  alertas: string[];
};

export type SeoCounts = {
  services: number;
  products: number;
  today: number;
  dailyCap: number;
};

export type SeoJob = {
  jobId: string;
  profileId: string;
  mode: "services" | "products" | "service_descriptions";
  status: "QUEUED" | "CLAIMED" | "RUNNING" | "COMPLETED" | "FAILED";
  dryRun: boolean;
  total: number;
  done: number;
  current: string;
  log: string[];
  error?: string;
};

export type GoogleConnection = {
  email: string | null;
  connected: boolean;
  needsReauthentication: boolean;
  hasToken: boolean;
};

export type BrowserStatusState =
  | "ONLINE_OK"
  | "ACCOUNT_MISSING"
  | "LOGIN_REQUIRED"
  | "WORKER_OFFLINE";

export type BrowserStatus = {
  workerOnline: boolean;
  expectedEmail: string | null;
  accountAvailable: boolean;
  state: BrowserStatusState;
};

export type StockImage = {
  id: number;
  thumbUrl: string;
  fullUrl: string;
  photographer: string | null;
  alt: string | null;
};

export type StockImageDataUrl = {
  dataUrl: string;
  contentType: string;
  name: string;
};

const data = <T>(response: { data?: { data?: T } }): T =>
  response.data?.data as T;

export const seoProfileApi = {
  async research(profileId: string, context?: string): Promise<SeoResearch> {
    return data<SeoResearch>(
      await ApiService.post("/seo/research", {
        profileId,
        context: context ?? "",
      }),
    );
  },

  async getLatestResearch(profileId: string): Promise<SeoResearch | null> {
    const res = await ApiService.get(`/seo/research/latest/${profileId}`);
    return (res?.data?.data as SeoResearch | undefined) ?? null;
  },

  async publish(payload: {
    profileId: string;
    mode: "services" | "products" | "service_descriptions";
    dryRun: boolean;
    operatorId?: string;
    items: Array<{
      name: string;
      description: string;
      requiresConfirmation: boolean;
      confirmed: boolean;
      imageName?: string;
      imageContentType?: string;
      imageDataUrl?: string;
      /** Botão do produto no Google (SAIBA_MAIS, COMPRAR...) e o link dele. */
      buttonType?: string;
      buttonUrl?: string;
    }>;
  }): Promise<SeoJob> {
    return data<SeoJob>(await ApiService.post("/seo/publish", payload));
  },

  async getStatus(jobId: string): Promise<SeoJob> {
    return data<SeoJob>(await ApiService.get(`/seo/status/${jobId}`));
  },

  async getCounts(profileId: string): Promise<SeoCounts> {
    return data<SeoCounts>(await ApiService.get(`/seo/counts/${profileId}`));
  },

  async getBrowserStatus(profileId: string): Promise<BrowserStatus> {
    return data<BrowserStatus>(
      await ApiService.get(`/seo/browser-status/${profileId}`),
    );
  },

  async searchStockImages(query: string, n = 9, page = 1): Promise<StockImage[]> {
    return data<StockImage[]>(
      await ApiService.get(`/seo/stock-images`, { params: { q: query, n, page } }),
    );
  },

  async stockImageToDataUrl(url: string): Promise<StockImageDataUrl> {
    return data<StockImageDataUrl>(
      await ApiService.get(`/seo/stock-images/data-url`, { params: { url } }),
    );
  },

  async browserLogin(profileId: string): Promise<BrowserStatus> {
    return data<BrowserStatus>(
      await ApiService.post(`/seo/browser-login/${profileId}`, {}),
    );
  },

  async getGoogleConnection(profileId: string): Promise<GoogleConnection> {
    return data<GoogleConnection>(
      await ApiService.get(`/business-profiles/${profileId}/google-connection`),
    );
  },

  reconnectGoogle(): void {
    const baseUrl = import.meta.env.VITE_APP_URL_BASE;
    if (baseUrl) {
      window.location.href = `${baseUrl}/imports-manager/oauth/google`;
    }
  },

  async getGabaritoKeywords(profileId: string): Promise<string[]> {
    const res = await ApiService.get(
      `/business-profiles/${profileId}/gabarito`,
    );
    const post: string = res?.data?.data?.postKeywords || "";
    return post
      .split(/[,;\n]/)
      .map((k: string) => k.trim())
      .filter(Boolean);
  },
};

/** Tarefa do robô da VPS para o perfil (tela Produtos). */
export type TarefaDoRobo = {
  id: string;
  /** product = publicar, product_list = ler a ficha, product_delete = apagar */
  tipo: "product" | "product_list" | "product_delete" | string;
  status: "PENDING" | "DISPATCHED" | "DONE" | "ERROR";
  nomes: string[];
  todos: boolean;
  simulacao: boolean;
  mensagem: string | null;
  criadoEm: string | null;
  terminadoEm: string | null;
};

export type ProdutosDaFicha = {
  /** Produtos na última leitura do robô; null = a ficha nunca foi lida. */
  naFicha: string[] | null;
  lidoEm: string | null;
  lendo: boolean;
  tarefas: TarefaDoRobo[];
};

/** Produtos não têm API no Google: quem lê e apaga é o robô da VPS (fila seo-ext). */
export const produtosApi = {
  async situacao(profileId: string): Promise<ProdutosDaFicha> {
    return data<ProdutosDaFicha>(await ApiService.get(`/seo/produtos/${profileId}`));
  },

  async ler(profileId: string): Promise<ProdutosDaFicha> {
    return data<ProdutosDaFicha>(await ApiService.post(`/seo/produtos/${profileId}/ler`, {}));
  },

  async apagar(profileId: string, pedido: { nomes?: string[]; todos?: boolean }): Promise<ProdutosDaFicha> {
    return data<ProdutosDaFicha>(
      await ApiService.post(`/seo/produtos/${profileId}/apagar`, {
        nomes: pedido.nomes ?? [],
        todos: !!pedido.todos,
      }),
    );
  },
};
